import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FaWindows } from "react-icons/fa";
import {
  FallbackImage,
  STATUS_FILTERS,
  SeoMeta,
  SiteFooter,
  SiteNavbar,
  formatDateTime,
  getStatusIndex,
  prefetchDataFile,
  preloadImage,
  useGameData,
} from "../../shared/site";
import { getGameById, STATUS_REFRESH_INTERVAL_MS } from "../../data/gameCatalog";
import "../../index.css";

const FILTER_CLASSES = {
  all: "border-purple-300/35 bg-purple-500/10 text-purple-100 shadow-[0_0_18px_rgba(216,180,254,0.12)]",
  undetected: "border-green-300/35 bg-green-500/15 text-green-100 shadow-[0_0_18px_rgba(34,197,94,0.12)]",
  updating: "border-yellow-300/35 bg-yellow-500/15 text-yellow-100 shadow-[0_0_18px_rgba(250,204,21,0.12)]",
  detected: "border-red-300/35 bg-red-500/15 text-red-100 shadow-[0_0_18px_rgba(248,113,113,0.12)]",
  frozen: "border-slate-300/25 bg-slate-500/12 text-slate-100",
};

const FILTER_LABELS = {
  all: "All",
  undetected: "Undetected",
  updating: "Updating",
  detected: "Detected",
  frozen: "Frozen",
};

const MODE_ORDER = ["rage mp", "five m", "alt v", "online"];
const MODE_LABELS = {
  "rage mp": "Rage MP",
  "five m": "FiveM",
  "alt v": "Alt:V",
  online: "Online",
};

const CARD_STATUS_BADGE_CLASSES = {
  undetected: "border-green-300/35 bg-green-500/15 text-green-100",
  updating: "border-yellow-300/35 bg-yellow-500/15 text-yellow-100",
  detected: "border-red-300/35 bg-red-500/15 text-red-100",
  frozen: "border-slate-300/25 bg-slate-500/15 text-slate-100",
};

const CARD_STATUS_BAR_CLASSES = {
  undetected: "bg-green-300",
  updating: "bg-yellow-300",
  detected: "bg-red-300",
  frozen: "bg-slate-300",
};

function StatusFilters({ selected, onSelect }) {
  return (
    <div className="w-full flex flex-nowrap justify-start gap-1.5 mb-3 overflow-x-auto px-0.5 py-0.5">
      {STATUS_FILTERS.map((status) => (
        <button
          key={status}
          type="button"
          onClick={() => onSelect(status)}
          className={`${FILTER_CLASSES[status]} flex-shrink-0 border px-2.5 py-1.5 md:px-3.5 md:py-2 rounded-md text-xs md:text-base font-bold leading-none transition hover:brightness-125 ${
            selected === status ? "ring-2 ring-purple-300" : ""
          }`}
        >
          {FILTER_LABELS[status]}
        </button>
      ))}
    </div>
  );
}

function ModeFilters({ modes, selected, onSelect }) {
  if (modes.length === 0) return null;

  return (
    <div className="w-full flex flex-wrap justify-end gap-3 mb-6">
      <button
        type="button"
        onClick={() => onSelect("all")}
      className={`px-5 py-2 rounded-md text-lg font-semibold transition border ${
          selected === "all"
            ? "bg-purple-500/20 text-purple-100 border-purple-300/60 shadow-[0_0_18px_rgba(216,180,254,0.15)]"
            : "bg-gray-950/60 text-gray-200 border-purple-400/20 hover:border-purple-300"
        }`}
      >
        All modes
      </button>
      {modes.map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => onSelect(mode)}
          className={`px-5 py-2 rounded-md text-lg font-semibold transition border ${
            selected === mode
              ? "bg-fuchsia-500/16 text-fuchsia-100 border-fuchsia-300/55 shadow-[0_0_18px_rgba(244,114,182,0.14)]"
              : "bg-gray-950/60 text-gray-200 border-purple-400/20 hover:border-fuchsia-300"
          }`}
        >
          {MODE_LABELS[mode] || mode}
        </button>
      ))}
    </div>
  );
}

function getItemModes(item) {
  if (Array.isArray(item.modes)) return item.modes;
  if (item.mode) return [item.mode];
  return [];
}

function getPreviewUrl(link) {
  const value = String(link || "");
  if (!value) return "";
  return `${value}${value.includes("?") ? "&" : "?"}preview=1`;
}

function sortWindowsVersions(os) {
  const order = ["10", "11"];
  return [...os].sort((a, b) => {
    const aIndex = order.indexOf(String(a));
    const bIndex = order.indexOf(String(b));

    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }

    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });
}

function productStatusLabel(status) {
  if (!status) return "Unknown";
  return String(status).charAt(0).toUpperCase() + String(status).slice(1);
}

function ProductCard({ item, game, onPreview }) {
  const navigate = useNavigate();
  const osList = Array.isArray(item.os) ? sortWindowsVersions(item.os) : [];
  const modeList = getItemModes(item);
  const productResult = { item, game };
  const statusBadgeClass = CARD_STATUS_BADGE_CLASSES[item.status] || "border-purple-300/30 bg-purple-500/15 text-purple-100";
  const statusBarClass = CARD_STATUS_BAR_CLASSES[item.status] || "bg-purple-300";
  const preloadProduct = () => {
    onPreview(item.link);
    prefetchDataFile(game?.dataFile);
    preloadImage(item.logo || game?.image);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.94, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 8 }}
      whileHover={{ x: 4, scale: 1.025 }}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      className="product-list-card group relative overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/65 p-3 shadow-lg transition hover:border-purple-300/45 hover:bg-gray-950/80 hover:shadow-purple-500/20 cursor-pointer"
      onMouseEnter={preloadProduct}
      onFocus={preloadProduct}
      onClick={() => navigate(item.link, { state: { productResult } })}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(item.link, { state: { productResult } });
        }
      }}
    >
      <span className={`absolute left-0 top-0 h-full w-1 ${statusBarClass}`} aria-hidden="true" />
      <div className="flex items-start gap-2.5 pl-1">
        <FallbackImage
          src={item.logo}
          alt={item.name}
          label={item.name}
          className="h-12 w-12 shrink-0 rounded-lg border border-purple-400/20 bg-black/25 object-cover shadow-[0_0_18px_rgba(2,6,23,0.35)] md:h-14 md:w-14"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 text-xl font-extrabold leading-tight text-purple-100 break-words">
              {item.name}
            </h3>
            <span className={`status-pulse status-${item.status} shrink-0 rounded-md border px-2 py-1 text-xs font-extrabold leading-none ${statusBadgeClass}`}>
              {productStatusLabel(item.status)}
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="rounded-md border border-purple-300/25 bg-purple-500/10 px-2 py-1 text-lg font-extrabold leading-none text-white">
              {item.price || "Ask support"}
            </span>
            {modeList.map((mode) => (
              <span key={mode} className="rounded-md border border-fuchsia-300/20 bg-fuchsia-500/10 px-2 py-1 text-sm font-bold leading-none text-fuchsia-100">
                {MODE_LABELS[mode] || mode}
              </span>
            ))}
            {osList.map((os) => (
              <span key={os} className="flex items-center rounded-md border border-sky-300/20 bg-sky-500/10 px-2 py-1 text-sm font-bold leading-none text-sky-100">
                <FaWindows className="mr-1" /> Win {os}
              </span>
            ))}
          </div>

          <div className="mt-1.5 text-sm leading-none text-gray-400">
            <span className="min-w-0 truncate">Updated {formatDateTime(item.lastUpdatedAt)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ProductListSkeleton() {
  return (
    <div className="themed-skeleton themed-skeleton-list grid grid-cols-1 gap-3" aria-hidden="true">
      <div className="themed-skeleton-console">
        <span className="themed-skeleton-led" />
        <span>syncing product nodes</span>
        <strong>LIVE</strong>
      </div>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={`category-skeleton-${index}`} className="themed-skeleton-card product-list-card h-[7rem] rounded-lg border border-purple-400/20 bg-gray-950/55 p-3">
          <span className="themed-skeleton-node" />
          <div className="flex items-start gap-2.5">
            <span className="skeleton-shimmer h-12 w-12 shrink-0 rounded-lg md:h-14 md:w-14" />
            <span className="min-w-0 flex-1 space-y-2">
              <span className="skeleton-shimmer block h-4 w-2/3 rounded" />
              <span className="skeleton-shimmer block h-3 w-1/2 rounded" />
              <span className="skeleton-shimmer block h-3 w-3/4 rounded" />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomGroupFilters({ groups, selected, onSelect, label = "Games" }) {
  if (!Array.isArray(groups) || groups.length === 0) return null;

  return (
    <div className="rounded-lg border border-purple-400/20 bg-gray-950/55 p-3 shadow-lg">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xl font-extrabold text-purple-100">{label}</p>
        <span className="rounded border border-purple-400/20 bg-black/25 px-2 py-1 text-sm font-bold text-gray-400">
          {groups.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSelect("all")}
          className={`rounded-md border px-3 py-2 text-base font-bold transition ${
            selected === "all"
              ? "border-purple-300/60 bg-purple-500/20 text-purple-100"
              : "border-purple-400/20 bg-black/30 text-gray-200 hover:border-purple-300/60"
          }`}
        >
          All
        </button>
        {groups.map((group) => (
          <button
            key={group.value}
            type="button"
            onClick={() => onSelect(group.value)}
            className={`rounded-md border px-3 py-2 text-base font-bold transition ${
              selected === group.value
                ? "border-purple-300/60 bg-purple-500/20 text-purple-100"
                : "border-purple-400/20 bg-black/30 text-gray-200 hover:border-purple-300/60"
            }`}
          >
            {group.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage({ game, customGroups, customGroupLabel = "Games" }) {
  const params = useParams();
  const resolvedGame = game || getGameById(params.gameId);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMode, setFilterMode] = useState("all");
  const [filterCustomGroup, setFilterCustomGroup] = useState("all");
  const [selectedLink, setSelectedLink] = useState(null);
  const [isCatalogScanning, setIsCatalogScanning] = useState(false);
  const { items: cheats, error, isLoading } = useGameData(
    resolvedGame?.dataFile,
    STATUS_REFRESH_INTERVAL_MS
  );

  const availableModes = useMemo(() => {
    const modeSet = new Set(cheats.flatMap(getItemModes));

    return Array.from(modeSet).sort((a, b) => {
      const aIndex = MODE_ORDER.indexOf(a);
      const bIndex = MODE_ORDER.indexOf(b);

      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [cheats]);

  const visibleCheats = useMemo(() => {
    return [...cheats]
      .filter((item) => (filterStatus === "all" ? true : item.status === filterStatus))
      .filter((item) => (filterMode === "all" ? true : getItemModes(item).includes(filterMode)))
      .filter((item) => {
        if (filterCustomGroup === "all" || !Array.isArray(customGroups) || customGroups.length === 0) return true;

        const selectedGroup = customGroups.find((group) => group.value === filterCustomGroup);
        return selectedGroup?.match ? selectedGroup.match(item) : true;
      })
      .sort((a, b) => getStatusIndex(a.status) - getStatusIndex(b.status));
  }, [cheats, customGroups, filterCustomGroup, filterMode, filterStatus]);

  useEffect(() => {
    if (visibleCheats.length === 0) {
      setSelectedLink(null);
      return;
    }

    if (!visibleCheats.some((item) => item.link === selectedLink)) {
      setSelectedLink(visibleCheats[0].link);
    }
  }, [selectedLink, visibleCheats]);

  useEffect(() => {
    if (isLoading) return undefined;
    setIsCatalogScanning(true);
    const timeoutId = setTimeout(() => setIsCatalogScanning(false), 420);
    return () => clearTimeout(timeoutId);
  }, [filterCustomGroup, filterMode, filterStatus, isLoading]);

  if (!resolvedGame || !resolvedGame.dataFile) {
    return (
      <div className="relative min-h-screen flex flex-col bg-transparent text-white font-jersey">
        <SeoMeta
          title={resolvedGame?.title || "Coming soon"}
          description={`${resolvedGame?.name || "This category"} is not available yet.`}
          image={resolvedGame?.image}
          canonicalPath={resolvedGame?.id ? `/games/${resolvedGame.id}` : ""}
        />
        <div className="relative z-10">
          <SiteNavbar animated={false} />
          <section className="pt-28 px-8 md:px-12 py-20 bg-purple-950/25 backdrop-blur-[2px] border-b border-purple-400/20 rounded-b-3xl shadow-[0_18px_60px_rgba(124,58,237,0.18)] text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {resolvedGame?.heading || "Coming soon"}
            </h2>
            <p className="text-gray-300 text-xl">This category is not available yet.</p>
          </section>
          <SiteFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col bg-transparent text-white font-jersey">
      <SeoMeta
        title={resolvedGame.title}
        description={`Browse ${resolvedGame.name} products, live status, prices and Windows compatibility.`}
        image={resolvedGame.image}
        canonicalPath={resolvedGame.canonicalPath || `/games/${resolvedGame.id}`}
      />
      <div className="relative z-10">
        <SiteNavbar animated={false} />

        <section className="cinematic-panel category-hero-panel pt-28 px-8 md:px-12 py-20 bg-purple-950/25 backdrop-blur-[2px] border-b border-purple-400/20 rounded-b-3xl shadow-[0_18px_60px_rgba(124,58,237,0.18)]">
          <nav className="flex items-center space-x-3 text-xl md:text-2xl font-bold mb-6 text-left">
            <Link to="/" className="flex items-center text-gray-300 hover:text-purple-400 transition-colors">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6"
                />
              </svg>
              Home
            </Link>

            <span className="text-gray-500 text-2xl">›</span>
            <span className="text-white font-extrabold drop-shadow-lg">{resolvedGame.title}</span>
          </nav>

          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-12 text-center">
            {resolvedGame.heading}
          </h2>

          {error && (
            <p className="mb-8 text-center text-yellow-300">
              Product data could not be loaded. The panel JSON path is still /data/{resolvedGame.dataFile}.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(19rem,1.2fr)_minmax(0,2.8fr)] gap-8 items-start">
            <div className="flex flex-col gap-4">
              <CustomGroupFilters
                groups={customGroups}
                selected={filterCustomGroup}
                onSelect={setFilterCustomGroup}
                label={customGroupLabel}
              />
              <StatusFilters selected={filterStatus} onSelect={setFilterStatus} />
              <ModeFilters modes={availableModes} selected={filterMode} onSelect={setFilterMode} />

              {isLoading && cheats.length === 0 ? (
                <ProductListSkeleton />
              ) : (
                <motion.div layout className={`catalog-scan-surface grid grid-cols-1 gap-3 ${isCatalogScanning ? "catalog-scan-active" : ""}`}>
                  <AnimatePresence>
                    {visibleCheats.map((item) => (
                      <ProductCard
                        key={`${item.name}-${item.link}`}
                        item={item}
                        game={resolvedGame}
                        onPreview={setSelectedLink}
                      />
                  ))}
                  </AnimatePresence>
                </motion.div>
              )}

              {!error && !isLoading && cheats.length > 0 && visibleCheats.length === 0 && (
                <p className="text-center text-gray-400 text-xl">No products found for this filter.</p>
              )}
            </div>

            <div className="category-preview-slot lg:self-stretch">
              <div className="category-preview-dock preview-stage w-full bg-gray-950/60 rounded-2xl shadow-lg border border-purple-400/20 overflow-hidden h-[640px]">
                {selectedLink ? (
                  <iframe
                    src={getPreviewUrl(selectedLink)}
                    title="Product preview"
                    className="w-full h-full"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p>Hover over a card to preview.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </div>
  );
}
